import React, { memo, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import Container from 'react-bootstrap/Container';
import Image from 'react-bootstrap/Image';
import { StatusCodes } from 'http-status-codes';
import { useDispatch } from 'react-redux';

import ErrorPage from './ErrorPage';
import LoadingPage from './LoadingPage';
import SystemMessagePage from './SystemMessagePage';
import ForbiddenPage from './ForbiddenPage';
import Header from '../Component/General/Header';
import InterestFixedPeriod from '../Component/Interest/InterestFixedPeriod';
import InterestMarketValue from '../Component/Interest/InterestMarketValue';
import InterestPerProduct from '../Component/Interest/InterestPerProduct';
import { default as InterestNews } from '../Component/Interest/News';
import BridgingLoan from '../Component/Interest/BridgingLoan';
import PoweredBy from '../Component/General/PoweredBy';
import Disclaimer from '../Component/General/Disclaimer';
import PropertiesPerLabel from '../Component/ProductProperties/PropertiesPerLabel';
import PropertiesPerProperty from '../Component/ProductProperties/PropertiesPerProperty';
import Sidebar from '../Component/Interest/Sidebar/Sidebar';
import SidebarToggler from '../Component/Interest/Sidebar/SidebarToggler';
import { default as PropertiesNews } from '../Component/ProductProperties/News';

import InterestUri from '../Enum/InterestUri';

import useAuthentication from '../Hook/Interest/UseAuthentication';
import useIsLoading from '../Hook/UseIsLoading';
import useGetError from '../Hook/UseGetError';
import useGetFavicon from '../Hook/UseGetFavicon';
import useGetLabels from '../Hook/Interest/UseGetLabels';
import useGetLastResponseCode from '../Hook/Interest/UseGetLastResponseCode';
import { getPageTitleBySection } from '../Helpers/PageTitle';
import isAllowed from '../Hook/Interest/UseAllowedUrl';
import { modalClosed } from '../Store/Modal/action';
import { scopeReset } from '../Store/Scope/action';

type Props = RouteComponentProps<{ section: InterestUri }>;

const isHeaderSection = (section: InterestUri): boolean =>
    ![InterestUri.InterestNews, InterestUri.Product, InterestUri.PropertiesNews].includes(section);

const InterestPage: React.FC<Props> = (props: Props) => {
    const { section } = props.match.params;

    useAuthentication();

    const error = useGetError();
    const loading = useIsLoading();
    const { activeLabel } = useGetLabels(true);
    const favIcon = useGetFavicon();
    const dispatch = useDispatch();

    useEffect(() => {
        const faviconElement = document.getElementById('favicon') as HTMLLinkElement;
        if (faviconElement) {
            faviconElement.href = favIcon;
        }

        const manifestElement = document.getElementById('manifest') as HTMLLinkElement;
        if (manifestElement) {
            manifestElement.href = '/manifest-producten.json';
        }

        const appleTouchIconElement = document.getElementById('apple-touch-icon') as HTMLLinkElement;
        if (appleTouchIconElement) {
            appleTouchIconElement.href = '/logos/adviesbox-180.png';
        }

        document.title = getPageTitleBySection(section);
        dispatch(modalClosed());

        if (section !== InterestUri.PropertiesPerLabel) {
            dispatch(scopeReset());
        }
    });

    const lastResponseCode = useGetLastResponseCode();

    if (lastResponseCode === StatusCodes.SERVICE_UNAVAILABLE) {
        return <SystemMessagePage />;
    }

    if (error !== null) {
        return <ErrorPage />;
    }

    if (loading) {
        return <LoadingPage />;
    }

    if (!isAllowed(section)) {
        return <ForbiddenPage message="De opgevraagde url is niet toegankelijk." />;
    }

    return (
        <>
            <Sidebar />
            <Header enablePdfExportButton={isHeaderSection(section)}>
                <span>
                    <SidebarToggler />
                    {section === InterestUri.Product && (
                        <>
                            <Image
                                src={activeLabel?.iconUrl}
                                alt={activeLabel?.maatschappij}
                                className="company-icon"
                            />
                            {activeLabel?.label}
                        </>
                    )}
                </span>
            </Header>
            <div className="interest-wrapper">
                <div className="main-wrapper">
                    <Container fluid className="interest-container">
                        {section === InterestUri.Product && <InterestPerProduct />}
                        {section === InterestUri.MarketValue && <InterestMarketValue />}
                        {section === InterestUri.FixedPeriod && <InterestFixedPeriod />}
                        {section === InterestUri.PropertiesPerLabel && <PropertiesPerLabel />}
                        {section === InterestUri.PropertiesPerProperty && <PropertiesPerProperty />}
                        {section === InterestUri.InterestNews && <InterestNews />}
                        {section === InterestUri.BridgingLoan && <BridgingLoan />}
                        {section === InterestUri.PropertiesNews && <PropertiesNews />}
                    </Container>
                </div>
                <Disclaimer />
                <PoweredBy />
            </div>
        </>
    );
};

export default memo(InterestPage);
